import { createContext, useContext, useEffect, useRef, useState, type FC } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Button, IconButton, Link, ProgressBar, SkeletonText, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader,
  TableRow, Tile,
} from '@carbon/react';
import { Download, Launch, PauseFilled, PlayFilledAlt } from '@carbon/icons-react';
import { GroupedBarChart, LineChart, SimpleBarChart } from '@carbon/charts-react';

import { stripEmDash } from '@maestra/core/wizard/limpar';
import { partesDaFala, semMarcadorIncompleto } from '@maestra/core/nucleo/arquivosNaFala';
import { graficoTemDados, numeroCurto, type AnexoDaNyta, type GraficoDaConversa } from '@maestra/core/nucleo/graficoDaConversa';
import { csv } from '@maestra/core/nucleo/leiturasDaNyta';
import { dominioDoLink, linkDaCelula, textoDaCelula, type TabelaDaFala } from '@maestra/core/nucleo/tabelasNaFala';

import { apoioDoArquivo, tempoDoAudio, useArquivoNaConversa } from '../../../logica/nyta/useArquivoNaConversa';
import type { ArquivoDeExemplo } from '../../../logica/publicas/conversaDeExemplo';
import { useTemaDoLugar } from '../../padroes/useTemaDaPagina';
import { VisualizadorDeArquivo } from '../../padroes/VisualizadorDeArquivo';
import styles from './Nyta.module.scss';

// O QUE A NYTA ESCREVE, desenhado: o texto em markdown e, no meio dele, o que ela cita (ver
// `nucleo/arquivosNaFala`): o arquivo do perfil vira um cartão (a imagem se vê, o áudio toca no
// player do sistema, o PDF abre no visualizador), o gráfico que o SERVIDOR calculou vira gráfico do
// Carbon (número nenhum sai do modelo) e a tabela vira uma tabela do Carbon, com o CSV.

const dataCurta = (x: string) => `${x.slice(8, 10)}/${x.slice(5, 7)}`;

type ArquivoPronto = Extract<ReturnType<typeof useArquivoNaConversa>, { estado: 'pronto' }>;

/**
 * Os arquivos da CONVERSA DE EXEMPLO (landing e Início de quem não assina): o caminho citado na
 * resposta vira o mesmo cartão, sem ir ao Storage. Fora do simulador o contexto é vazio, e a
 * conversa de verdade só lê o perfil.
 */
export const ArquivosDeExemplo = createContext<{ arquivos: Record<string, ArquivoDeExemplo>; baixar: () => void } | null>(null);

/** O cartão do arquivo: o mesmo desenho para o arquivo do perfil e para o do exemplo. */
const CartaoDoArquivo: FC<{ a: ArquivoPronto }> = ({ a }) => (
  <Tile className={styles.parte}>
    {a.tipo === 'imagem' && a.link && a.visualizador && (
      <Link href={a.link} className={styles.imagem} onClick={(e: React.MouseEvent) => { e.preventDefault(); void a.visualizador?.abrir(); }}>
        <img src={a.link} alt={a.nome} loading='lazy' />
      </Link>
    )}
    {a.tipo === 'video' && a.link && (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video className={styles.video} src={a.link} controls preload='metadata' />
    )}
    <div className={styles.cabecaDaParte}>
      <span className={styles.nomeDaParte}>
        <span className={styles.nomeDoArquivo}>{a.nome}</span>
        <span className={styles.apoio}>{a.apoio}</span>
      </span>
      <span className={styles.acoesDaParte}>
        {a.tipo === 'pdf' && a.visualizador && (
          <IconButton kind='ghost' size='sm' label={`Abrir ${a.nome}`} onClick={() => { void a.visualizador?.abrir(); }}><Launch /></IconButton>
        )}
        <IconButton kind='ghost' size='sm' label={`Baixar ${a.nome}`} onClick={() => { void a.baixar(); }}><Download /></IconButton>
      </span>
    </div>
    {a.tocador && (
      <div className={styles.tocador}>
        <IconButton kind='primary' size='sm' label={`${a.tocador.tocando ? 'Pausar' : 'Tocar'} ${a.nome}`} onClick={a.tocador.alternar}>
          {a.tocador.tocando ? <PauseFilled /> : <PlayFilledAlt />}
        </IconButton>
        <ProgressBar className={styles.andamentoDoAudio} label={`Andamento de ${a.nome}`} hideLabel value={a.tocador.andamento} max={100} size='small' />
        {a.tocador.tempo && <span className={styles.numero}>{a.tocador.tempo}</span>}
      </div>
    )}
    {a.trecho && <pre className={styles.trecho}>{a.trecho}</pre>}
    {a.visualizador && (
      <VisualizadorDeArquivo itens={a.visualizador.itens} aberto={a.visualizador.aberto} trocar={() => undefined}
        fechar={a.visualizador.fechar} baixar={() => { void a.baixar(); }} />
    )}
  </Tile>
);

/** O arquivo do perfil, lido com a sessão de quem está na conversa. */
const ArquivoDoPerfil: FC<{ artistId?: string | null; caminho: string }> = ({ artistId, caminho }) => {
  const a = useArquivoNaConversa(artistId, caminho);
  if (a.estado === 'carregando') return <Tile className={styles.parte}><SkeletonText paragraph lineCount={2} /></Tile>;
  if (a.estado === 'sumiu') return <Tile className={styles.parte}><p className={styles.apoio}>Este arquivo não está mais no perfil.</p></Tile>;
  return <CartaoDoArquivo a={a} />;
};

/**
 * O arquivo do exemplo: o mesmo cartão, com o player contando o tempo aqui mesmo. Não toca som (o
 * exemplo não tem áudio) e não abre o player da página: no Início, ele é o do perfil de verdade.
 * O player começa sozinho, para a cena mostrar a música tocando na conversa.
 */
const ArquivoDoExemplo: FC<{ arquivo: ArquivoDeExemplo; baixar: () => void }> = ({ arquivo, baixar }) => {
  const [segundos, setSegundos] = useState(0);
  const [tocando, setTocando] = useState(false);
  const [comecou, setComecou] = useState(false);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setTimeout(() => { setComecou(true); setTocando(true); }, 700);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!tocando) return undefined;
    const t = setInterval(() => setSegundos((s) => Math.min(arquivo.duracao, s + 1)), 1000);
    return () => clearInterval(t);
  }, [tocando, arquivo.duracao]);
  useEffect(() => { if (segundos >= arquivo.duracao) setTocando(false); }, [segundos, arquivo.duracao]);
  const a: ArquivoPronto = {
    estado: 'pronto', nome: arquivo.nome, apoio: apoioDoArquivo(arquivo), link: null, trecho: null, tipo: 'audio', visualizador: null,
    baixar: async () => { baixar(); },
    tocador: {
      tocando,
      andamento: Math.min(100, (segundos / arquivo.duracao) * 100),
      tempo: comecou ? `${tempoDoAudio(segundos)} de ${tempoDoAudio(arquivo.duracao)}` : null,
      alternar: () => {
        setComecou(true);
        if (segundos >= arquivo.duracao) setSegundos(0);
        setTocando((t) => !t);
      },
    },
  };
  return <CartaoDoArquivo a={a} />;
};

const ArquivoNaFala: FC<{ artistId?: string | null; caminho: string }> = ({ artistId, caminho }) => {
  const exemplo = useContext(ArquivosDeExemplo);
  const doExemplo = exemplo?.arquivos[caminho];
  return doExemplo ? <ArquivoDoExemplo arquivo={doExemplo} baixar={exemplo.baixar} /> : <ArquivoDoPerfil artistId={artistId} caminho={caminho} />;
};

const GraficoNaFala: FC<{ grafico?: GraficoDaConversa }> = ({ grafico }) => {
  // O tema do lugar, e não o da página: numa zona escura por desenho o gráfico sai escuro também.
  const lugar = useRef<HTMLDivElement>(null);
  const tema = useTemaDoLugar(lugar);
  if (!grafico || !graficoTemDados(grafico)) {
    return <Tile className={styles.parte}><p className={styles.apoio}>Não há dados suficientes para desenhar este gráfico.</p></Tile>;
  }
  const comum = {
    theme: tema,
    height: '240px',
    resizable: true,
    toolbar: { enabled: false },
    accessibility: { svgAriaLabel: grafico.titulo },
    legend: { enabled: grafico.series.length > 1 },
    tooltip: { valueFormatter: (v: unknown) => (typeof v === 'number' ? `${v.toLocaleString('pt-BR')}${grafico.unidade ? ` ${grafico.unidade}` : ''}` : String(v)) },
  };
  let desenho: React.ReactNode;
  if (grafico.tipo === 'linha') {
    const dados = grafico.series.flatMap((s) => s.pontos.map((p) => ({ group: s.nome, date: new Date(`${p.x.slice(0, 10)}T12:00:00`), value: p.y })));
    desenho = (
      <LineChart data={dados} options={{
        ...comum,
        axes: {
          bottom: { mapsTo: 'date', scaleType: 'time', ticks: { formatter: (d: unknown) => dataCurta((d as Date).toISOString()) } },
          left: { mapsTo: 'value', ticks: { formatter: (v: unknown) => numeroCurto(Number(v)) } },
        },
        curve: 'curveMonotoneX',
      } as never} />
    );
  } else {
    const dados = grafico.series.flatMap((s) => grafico.categorias.map((c, i) => ({ group: s.nome, key: c, value: s.valores[i] ?? 0 })));
    const opcoes = {
      ...comum,
      axes: {
        left: { mapsTo: 'key', scaleType: 'labels', truncation: { type: 'none' } },
        bottom: { mapsTo: 'value', ticks: { formatter: (v: unknown) => numeroCurto(Number(v)), rotation: 'never' } },
      },
    } as never;
    desenho = grafico.series.length > 1 ? <GroupedBarChart data={dados} options={opcoes} /> : <SimpleBarChart data={dados} options={opcoes} />;
  }
  return (
    <Tile className={styles.parte}>
      <p className={styles.tituloDaParte}>{grafico.titulo}</p>
      <div className={styles.grafico} ref={lugar}>{desenho}</div>
      {grafico.tipo === 'linha' && grafico.marcos && grafico.marcos.length > 0 && (
        <ul className={styles.marcos}>
          {grafico.marcos.map((m) => <li key={`${m.x}-${m.rotulo}`}><span className={styles.numero}>{dataCurta(m.x)}</span> {m.rotulo}</li>)}
        </ul>
      )}
      <p className={styles.apoio}>{`Fonte: ${grafico.fonte}`}</p>
    </Tile>
  );
};

const TabelaNaFala: FC<{ tabela: TabelaDaFala }> = ({ tabela }) => {
  const baixar = () => {
    const texto = csv(tabela.cabecalho.map(textoDaCelula), tabela.linhas.map((l) => l.map(textoDaCelula)));
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Nyta - ${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className={tabela.cabecalho.length >= 3 ? `${styles.parteTabela} ${styles.empilhavel}` : styles.parteTabela}>
      <TableContainer>
        <Table size='sm' aria-label='Tabela da resposta da Nyta'>
          <TableHead>
            <TableRow>
              {/* eslint-disable-next-line react/no-array-index-key */}
              {tabela.cabecalho.map((c, i) => <TableHeader key={i}>{textoDaCelula(c)}</TableHeader>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {tabela.linhas.map((linha, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <TableRow key={i}>
                {linha.map((c, j) => {
                  const link = linkDaCelula(c);
                  const texto = textoDaCelula(c);
                  const vazio = !texto || /^n[aã]o encontrado/i.test(texto);
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableCell key={j} className={vazio ? styles.apoio : undefined} data-rotulo={textoDaCelula(tabela.cabecalho[j] ?? '')}>
                      {link ? <Link href={link} target='_blank' rel='noopener noreferrer'>{dominioDoLink(link)}</Link> : (texto || '—')}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles.rodapeDaTabela}>
        <span className={styles.apoio}>{`${tabela.linhas.length} ${tabela.linhas.length === 1 ? 'linha' : 'linhas'}`}</span>
        <Button kind='ghost' size='sm' renderIcon={Download} onClick={baixar}>Baixar em CSV</Button>
      </div>
    </div>
  );
};

/** A resposta da Nyta, com o que ela cita desenhado no meio do texto. */
export const FalaDaNyta: FC<{ texto: string; artistId?: string | null; anexos?: Record<string, AnexoDaNyta> }> = ({ texto, artistId, anexos }) => (
  <div className={styles.fala}>
    {partesDaFala(semMarcadorIncompleto(texto)).map((parte, i) => {
      // eslint-disable-next-line react/no-array-index-key
      if (parte.tipo === 'arquivo') return <ArquivoNaFala key={`${i}-${parte.caminho}`} artistId={artistId} caminho={parte.caminho} />;
      // eslint-disable-next-line react/no-array-index-key
      if (parte.tipo === 'tabela') return <TabelaNaFala key={`${i}-tabela`} tabela={parte.tabela} />;
      // eslint-disable-next-line react/no-array-index-key
      if (parte.tipo === 'grafico') return <GraficoNaFala key={`${i}-${parte.id}`} grafico={anexos?.[parte.id]?.grafico} />;
      // eslint-disable-next-line react/no-array-index-key
      // `img` não se desenha no texto da Nyta: uma imagem markdown faz o navegador buscar a URL
      // sozinho, e uma injeção num dado do perfil poderia mandar dados para fora por ela (QA da
      // Nyta, SEC-04). Imagem do perfil aparece pelo cartão de arquivo, nunca pelo markdown.
      return <div key={i} className={styles.markdown}><ReactMarkdown disallowedElements={['img']} unwrapDisallowed>{stripEmDash(parte.texto)}</ReactMarkdown></div>;
    })}
  </div>
);
